import { SortableHeadCell, TextCell } from 'components/tables/cells';
import { createColumnHelper } from '@tanstack/table-core';
import { TextColumnProps } from 'components/tables/columnTypes';
import { CompanyRealtimeAccess } from '../types';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import LinkCell from 'components/tables/cells/LinkCell/LinkCell';

const columnHelper = createColumnHelper<CompanyRealtimeAccess>();

export const companyLevelTableColumns = {
	id: (overrideProps?: TextColumnProps<CompanyRealtimeAccess>) =>
		columnHelper.accessor((row) => `${row.companyId}`, {
			id: 'companyId',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="ID"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue()}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	youniumAccountNumber: (
		overrideProps?: TextColumnProps<CompanyRealtimeAccess>,
	) =>
		columnHelper.accessor((row) => row.youniumAccountNumber, {
			id: 'youniumAccountNumber',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Younium Account Number"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue()}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	hubspotId: (overrideProps?: TextColumnProps<CompanyRealtimeAccess>) =>
		columnHelper.accessor(
			(row) => (row.hubspotId != null ? `${row.hubspotId}` : '-'),
			{
				id: 'hubspotId',
				enableSorting: true,
				header: ({ header }) => (
					<SortableHeadCell
						text="Hubspot ID"
						header={header}
						{...overrideProps?.headCellProps}
					/>
				),
				cell: (info) => (
					<TextCell
						text={info.getValue() || '-'}
						{...overrideProps?.cellProps}
					/>
				),
				...overrideProps?.columnDefProps,
			},
		),
	companyName: (overrideProps?: TextColumnProps<CompanyRealtimeAccess>) =>
		columnHelper.accessor((row) => row.companyName, {
			id: 'companyName',
			enableSorting: true,

			header: ({ header }) => (
				<SortableHeadCell
					text="Company Name"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue()}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	highestUserCount: (
		overrideProps?: TextColumnProps<CompanyRealtimeAccess>,
	) =>
		columnHelper.accessor((row) => `${row.highestUserCount}`, {
			id: 'highestUserCount',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Highest User Count"
					tooltipText="Highest number of users with access during the specified period"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={`${info.getValue() || '-'}`}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	lowestUserCount: (overrideProps?: TextColumnProps<CompanyRealtimeAccess>) =>
		columnHelper.accessor((row) => `${row.lowestUserCount}`, {
			id: 'lowestUserCount',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Lowest User Count"
					tooltipText="Lowest number of users with access during the specified period"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={`${info.getValue() || '-'}`}
					withTooltip
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	endDateUserCount: (
		overrideProps?: TextColumnProps<CompanyRealtimeAccess>,
	) =>
		columnHelper.accessor((row) => `${row.endDateUserCount}`, {
			id: 'endDateUserCount',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="End Date User Count"
					tooltipText="Number of users with access at the end of the specified period"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={`${info.getValue() || '-'}`}
					withTooltip
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	realtime: (overrideProps?: TextColumnProps<CompanyRealtimeAccess>) =>
		columnHelper.accessor((row) => `${row.realtime}`, {
			id: 'realtime',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Realtime"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue() == 'true' ? 'Yes' : ''}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	subscriptionType: (
		overrideProps?: TextColumnProps<CompanyRealtimeAccess>,
	) =>
		columnHelper.accessor((row) => `${row.subscriptionType}`, {
			id: 'subscriptionType',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Subscription Type"
					tooltipText="Type of subscription for the company. If multiple subscriptions with varying types, the one of highest priority (Paid, Trial, Free, Internal) is specified."
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue() || '-'}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	grantedAccess: (overrideProps?: TextColumnProps<CompanyRealtimeAccess>) =>
		columnHelper.accessor((row) => row.grantedAccess, {
			id: 'grantedAccess',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Granted Access"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={toDefaultDateFormatOrNotAvailable(
						info.getValue(),
						'n/a',
						'DD MM YYYY',
					)}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	lostAccess: (overrideProps?: TextColumnProps<CompanyRealtimeAccess>) =>
		columnHelper.accessor((row) => row.lostAccess || '-', {
			id: 'lostAccess',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Lost Access"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={toDefaultDateFormatOrNotAvailable(
						info.getValue(),
						'Active',
						'DD MM YYYY',
					)}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	youniumLink: (overrideProps?: TextColumnProps<CompanyRealtimeAccess>) =>
		columnHelper.accessor((row) => row.youniumLink || '-', {
			id: 'youniumLink',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Younium Link"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<LinkCell
					url={info.getValue() || ''}
					text={info.getValue() || '-'}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
};
